import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { withRouter, Switch as RouterSwitch } from 'react-router-dom'
import {
  AutoComplete,
  Button,
  Modal,
  Table,
  Tag,
  Input,
  Select,
  Switch,
  Space,
  Empty,
  Popconfirm,
  Row,
  Col
} from 'antd'
import PageLoader from '../../../components/Layouts/PageLoader'
import PrivateRoute from '../../../hocs/PrivateRoute'
import PaginationContainer from '../../../components/Forms/Pagination'
import {
  getRequest,
  postRequest,
  patchRequest,
  deleteRequest,
  formatUrl,
  dutyDriveTrainBadgesUrl,
  carMakesUrl,
  successNotification,
  errorNotification
} from '../../../config'

const { Option } = Select

const BLANK = {
  pattern: '',
  make: undefined,
  polarity: 'AWD',
  excludes: '',
  allowSuffix: false,
  isActive: true,
  notes: ''
}

// The API answers a bad entry with DRF field errors ({"pattern": ["..."]}). Surfacing the actual
// message is the difference between "something went wrong" and "this badge already exists for
// that make" — these are the guard rails that stop a curator breaking intake.
const fieldError = error => {
  const data = error?.response?.data
  if (!data || typeof data !== 'object') return null
  const key = Object.keys(data)[0]
  if (!key) return null
  const value = data[key]
  return Array.isArray(value) ? value[0] : String(value)
}

const DriveTrainBadgesList = () => {
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [meta, setMeta] = useState({ previous: null, next: null, count: 0 })
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState(null) // record being edited, or {} for new
  const [form, setForm] = useState(BLANK)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState(null)
  const [makes, setMakes] = useState([])

  const getRows = async (url = null) => {
    const base = search
      ? `${dutyDriveTrainBadgesUrl}?search=${encodeURIComponent(search)}`
      : dutyDriveTrainBadgesUrl
    const response = await getRequest(url ? formatUrl(url) : base, 'badges', setLoading)
    if (response.success) {
      setRows(response.data.results || response.data)
      setMeta({ ...response.data })
    }
  }

  useEffect(() => {
    getRows()
    const loadMakes = async () => {
      const mk = await getRequest(`${carMakesUrl}/all`, 'makes', () => {})
      // The badge stores a brand NAME, not an id — this endpoint is only a source of spellings,
      // and a badge may name a brand nobody has quoted yet (hence AutoComplete, not Select).
      if (mk.success) setMakes((mk.data || []).map(m => ({ value: m.name })))
    }
    loadMakes()
    // eslint-disable-next-line
  }, [])

  const setField = key => value => setForm(f => ({ ...f, [key]: value }))

  const openNew = () => {
    setForm(BLANK)
    setFormError(null)
    setEditing({})
  }

  const openEdit = record => {
    setForm({
      pattern: record.pattern || '',
      make: record.make || undefined,
      polarity: record.polarity || 'AWD',
      excludes: record.excludes || '',
      allowSuffix: !!record.allowSuffix,
      isActive: record.isActive,
      notes: record.notes || ''
    })
    setFormError(null)
    setEditing(record)
  }

  const handleSave = async () => {
    setFormError(null)
    if (!form.pattern.trim()) {
      errorNotification('Enter the badge text')
      return
    }
    const payload = { ...form, make: form.make || '' }
    const isNew = !editing.id
    const res = isNew
      ? await postRequest(dutyDriveTrainBadgesUrl + '/', payload, 'badge', setSaving)
      : await patchRequest(
          `${dutyDriveTrainBadgesUrl}/${editing.id}/`,
          payload,
          'badge',
          setSaving
        )
    if (res.success) {
      successNotification(isNew ? 'Badge added' : 'Badge updated')
      setEditing(null)
      getRows()
      return
    }
    const detail = fieldError(res.error)
    if (detail) {
      setFormError(detail)
      errorNotification(detail)
    }
  }

  const handleDelete = async record => {
    const res = await deleteRequest(
      `${dutyDriveTrainBadgesUrl}/${record.id}/`,
      'badge',
      setLoading
    )
    if (res.success) {
      successNotification('Badge removed')
      getRows()
    }
  }

  /* eslint-disable react/display-name */
  const columns = [
    {
      title: 'Badge',
      key: 'pattern',
      render: (_, r) => (
        <span>
          <b>{r.pattern}</b>
          {!r.isActive && (
            <Tag color="red" style={{ marginLeft: 8 }}>
              Inactive
            </Tag>
          )}
        </span>
      )
    },
    {
      title: 'Make',
      key: 'make',
      render: (_, r) => r.make || <span style={{ color: '#aaa' }}>Any</span>
    },
    {
      title: 'Means',
      key: 'polarity',
      width: 100,
      render: (_, r) => (
        <Tag color={r.polarity === 'AWD' ? 'green' : 'default'}>{r.polarity}</Tag>
      )
    },
    {
      title: 'Matching',
      key: 'matching',
      render: (_, r) => {
        const parts = []
        if (r.allowSuffix) parts.push('Runs into trim')
        if (r.excludes) parts.push(`Not with: ${r.excludes}`)
        return parts.length ? (
          <Space size={[0, 4]} wrap>
            {parts.map((p, i) => (
              <Tag key={i}>{p}</Tag>
            ))}
          </Space>
        ) : (
          <span style={{ color: '#aaa' }}>—</span>
        )
      }
    },
    { title: 'Notes', dataIndex: 'notes', key: 'notes', ellipsis: true },
    {
      title: 'Action',
      key: 'action',
      width: 170,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => openEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Remove this badge?"
            onConfirm={() => handleDelete(record)}
            okText="Remove"
            cancelText="Cancel"
          >
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]
  /* eslint-enable react/display-name */

  return (
    <PageLoader className="container" loading={loading}>
      <div className="card mb-5rem">
        <div className="card-body">
          <h3 style={{ marginBottom: 8 }}>
            Drive Train Badges {meta && `(${meta.count || rows.length})`}
          </h3>
          <p style={{ color: '#888', marginBottom: 16 }}>
            Manufacturer names for a drive-train system — Mercedes <b>4MATIC</b>, BMW{' '}
            <b>xDrive</b>, Audi <b>quattro</b>. When a customer&apos;s car description contains
            one, the assistant sets the drive train from it instead of asking whether the car is
            AWD. A few badges mean the opposite: BMW <b>sDrive</b> is 2WD. Deactivate a wrong badge
            rather than deleting it, so the decision stays on record.
          </p>
          <Space wrap style={{ marginBottom: 16 }}>
            <Input.Search
              allowClear
              placeholder="Search badge / make / notes"
              style={{ width: 260 }}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onSearch={() => getRows()}
            />
            <Button type="primary" onClick={openNew}>
              + Add Badge
            </Button>
            <Button onClick={() => getRows()}>Refresh</Button>
          </Space>

          <Table
            rowKey="id"
            dataSource={rows}
            columns={columns}
            pagination={false}
            scroll={{ x: 800 }}
            locale={{ emptyText: <Empty description="No badges yet" /> }}
          />
          {rows.length ? (
            <PaginationContainer meta={meta} getData={url => getRows(url)} />
          ) : null}
        </div>
      </div>

      <Modal
        title={editing?.id ? 'Edit badge' : 'Add badge'}
        centered
        open={!!editing}
        onOk={handleSave}
        onCancel={() => setEditing(null)}
        okText="Save"
        confirmLoading={saving}
        width={640}
      >
        <Row gutter={[12, 12]}>
          <Col xs={24} md={12}>
            <label>Badge</label>
            <Input
              placeholder="e.g. 4MATIC, xDrive, SH-AWD"
              value={form.pattern}
              onChange={e => setField('pattern')(e.target.value)}
            />
          </Col>
          <Col xs={24} md={12}>
            <label>Make</label>
            <AutoComplete
              allowClear
              style={{ width: '100%' }}
              placeholder="Any make"
              options={makes}
              value={form.make}
              onChange={setField('make')}
              filterOption={(input, option) =>
                (option?.value || '').toUpperCase().includes(input.toUpperCase())
              }
            />
          </Col>
          <Col xs={24}>
            <p style={{ color: '#888', fontSize: 12, margin: '4px 0 0' }}>
              Scope brand-specific badges to their make. It is what keeps <b>Q4</b> (Alfa Romeo /
              Maserati, AWD) from firing on an <b>Audi Q4</b> e-tron, where Q4 is the model name.
              Leave the make blank only for universal codes like AWD or 4X4. You can type a brand
              that is not in the list.
            </p>
          </Col>
          <Col xs={24} md={12}>
            <label>Means</label>
            <Select
              style={{ width: '100%' }}
              value={form.polarity}
              onChange={setField('polarity')}
            >
              <Option value="AWD">AWD / 4WD</Option>
              <Option value="2WD">2WD</Option>
            </Select>
          </Col>
          <Col xs={24} md={12}>
            <label>Never match alongside</label>
            <Input
              placeholder="e.g. QUATTROPORTE"
              value={form.excludes}
              onChange={e => setField('excludes')(e.target.value)}
            />
          </Col>
          <Col xs={24} md={12}>
            <label style={{ display: 'block' }}>Badge runs into the trim</label>
            <Switch checked={form.allowSuffix} onChange={setField('allowSuffix')} />
            <p style={{ color: '#888', fontSize: 12, margin: '4px 0 0' }}>
              Turn on only when the brand writes it glued to the trim, as BMW does with
              <b> xDrive40i</b>. Leaving it off is what stops <b>quattro</b> matching
              <b> Quattroporte</b>.
            </p>
          </Col>
          <Col xs={24} md={12}>
            <label style={{ display: 'block' }}>Active</label>
            <Switch checked={form.isActive} onChange={setField('isActive')} />
          </Col>
          <Col xs={24}>
            <label>Notes</label>
            <Input.TextArea
              rows={2}
              value={form.notes}
              onChange={e => setField('notes')(e.target.value)}
            />
          </Col>
          {formError && (
            <Col xs={24}>
              <p style={{ color: '#ff4d4f', margin: 0 }}>{formError}</p>
            </Col>
          )}
        </Row>
      </Modal>
    </PageLoader>
  )
}

const DriveTrainBadgesRoutes = props => (
  <RouterSwitch>
    <PrivateRoute exact path={props.match.path} component={DriveTrainBadgesList} />
  </RouterSwitch>
)

DriveTrainBadgesRoutes.propTypes = {
  match: PropTypes.object
}

export default withRouter(DriveTrainBadgesRoutes)
